import React, { useState, useEffect } from 'react';
import { Container, Card, Tabs, Tab, Form, Button, Alert, Spinner, Badge, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageTitleContext';
import { generateSlug } from '../utils/slugUtils';
import { groupReviewsByVisit, reviewDishName } from '../utils/reviewGrouping';
import DishPhoto from './DishPhoto';
import { groupPhotosByDish, visitDishLabel } from '../utils/visitPhotos';
import PrivacySettings from './PrivacySettings';
import FollowersList from './FollowersList';
import EditVisitModal from './EditVisitModal';
import EditReviewModal from './EditReviewModal';
import { ownershipStatus } from '../utils/ownership';

function UserProfile() {
  const { user, token, isAuthenticated, logout } = useAuth();
  const { setPageTitle } = usePageTitle();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [activitySubTab, setActivitySubTab] = useState('my-restaurants');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile data
  const [profileData, setProfileData] = useState({
    email: '',
    username: '',
    first_name: '',
    last_name: '',
    display_name: '',
    bio: '',
    location: '',
    website_url: '',
    avatar_url: '',
  });

  // Password change data
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  // User's restaurants
  const [myRestaurants, setMyRestaurants] = useState([]);
  const [restaurantsLoading, setRestaurantsLoading] = useState(false);

  // User's reviews
  const [myReviews, setMyReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [showEditReviewModal, setShowEditReviewModal] = useState(false);

  // User's favorites
  const [myFavorites, setMyFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);

  // User's wishlist
  const [myWishlist, setMyWishlist] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  // Account deletion
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // User's visits
  const [myVisits, setMyVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [showEditVisitModal, setShowEditVisitModal] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchUserProfile();
  }, [isAuthenticated, navigate]);

  // Set page title for Profile page
  useEffect(() => {
    setPageTitle('My Profile');
  }, [setPageTitle]);

  const fetchUserProfile = async () => {
    setLoading(true);
    try {
      // Fetch basic user info
      const basicResponse = await fetch(`${API_BASE_URL}/users/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!basicResponse.ok) {
        throw new Error('Failed to fetch profile');
      }

      const basicData = await basicResponse.json();

      // Fetch extended profile data
      try {
        const extendedResponse = await fetch(`${API_BASE_URL}/users/social/profile/${user.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (extendedResponse.ok) {
          const extendedData = await extendedResponse.json();
          setProfileData({
            email: basicData.email || '',
            username: basicData.username || '',
            first_name: basicData.first_name || '',
            last_name: basicData.last_name || '',
            display_name: extendedData.display_name || '',
            bio: extendedData.bio || '',
            location: extendedData.location || '',
            website_url: extendedData.website_url || '',
            avatar_url: extendedData.avatar_url || '',
          });
        } else {
          // If extended profile doesn't exist yet, just use basic data
          setProfileData({
            email: basicData.email || '',
            username: basicData.username || '',
            first_name: basicData.first_name || '',
            last_name: basicData.last_name || '',
            display_name: '',
            bio: '',
            location: '',
            website_url: '',
            avatar_url: '',
          });
        }
      } catch (extErr) {
        // If extended profile fetch fails, just use basic data
        setProfileData({
          email: basicData.email || '',
          username: basicData.username || '',
          first_name: basicData.first_name || '',
          last_name: basicData.last_name || '',
          display_name: '',
          bio: '',
          location: '',
          website_url: '',
          avatar_url: '',
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyRestaurants = async () => {
    if (myRestaurants.length > 0) return; // Already loaded

    setRestaurantsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/restaurants/?created_by_user_id=${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch restaurants');
      }

      const data = await response.json();
      setMyRestaurants(data);
    } catch (err) {
      console.error('Error fetching restaurants:', err);
    } finally {
      setRestaurantsLoading(false);
    }
  };

  const fetchMyReviews = async () => {
    setReviewsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/reviews/?user_id=${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch reviews');
      }

      const data = await response.json();
      setMyReviews(data);
    } catch (err) {
      console.error('Error fetching reviews:', err);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setShowEditReviewModal(true);
  };

  const handleReviewUpdated = () => {
    fetchMyReviews(); // Refresh the list
    setShowEditReviewModal(false);
    setEditingReview(null);
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete review');
      }

      fetchMyReviews(); // Refresh the list
    } catch (err) {
      alert(err.message || 'Failed to delete review');
    }
  };

  const fetchMyFavorites = async () => {
    if (myFavorites.length > 0) return; // Already loaded

    setFavoritesLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/favorites/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch favorites');
      }

      const data = await response.json();
      setMyFavorites(data);
    } catch (err) {
      console.error('Error fetching favorites:', err);
    } finally {
      setFavoritesLoading(false);
    }
  };

  const fetchMyWishlist = async () => {
    if (myWishlist.length > 0) return; // Already loaded

    setWishlistLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/wishlist/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch wishlist');
      }

      const data = await response.json();
      setMyWishlist(data);
    } catch (err) {
      console.error('Error fetching wishlist:', err);
    } finally {
      setWishlistLoading(false);
    }
  };

  const fetchMyVisits = async () => {
    setVisitsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/visits/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch visits');
      }

      const data = await response.json();
      setMyVisits(data.visits || []);
    } catch (err) {
      console.error('Error fetching visits:', err);
    } finally {
      setVisitsLoading(false);
    }
  };

  const handleEditVisit = (visit) => {
    setEditingVisit(visit);
    setShowEditVisitModal(true);
  };

  const handleVisitUpdated = () => {
    fetchMyVisits(); // Refresh the list
    setShowEditVisitModal(false);
    setEditingVisit(null);
  };

  const handleDeleteVisit = async (visitId) => {
    if (!window.confirm('Are you sure you want to delete this visit?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/visits/${visitId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete visit');
      }

      fetchMyVisits(); // Refresh the list
    } catch (err) {
      alert(err.message || 'Failed to delete visit');
    }
  };

  const handleToggleVisitPublic = async (visit) => {
    try {
      // When making a visit public (#91), offer to share its photos too — all or
      // nothing, so a private receipt/bill photo isn't exposed without a choice.
      let sharePhotos = false;
      const makingPublic = !visit.is_public;
      const photoCount = visit.photos ? visit.photos.length : 0;
      if (makingPublic && photoCount > 0) {
        sharePhotos = window.confirm(
          `Also share your ${photoCount} photo${photoCount === 1 ? '' : 's'} on this public review? ` +
          `They'll be visible to everyone (you can report/remove later). ` +
          `Choose Cancel to post the review without photos.`
        );
      }
      const url = `${API_BASE_URL}/api/v1/visits/${visit.id}/toggle-public` +
        (sharePhotos ? '?share_photos=true' : '');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to toggle visit visibility');
      }

      fetchMyVisits(); // Refresh the list to show updated public/private status
    } catch (err) {
      alert(err.message || 'Failed to toggle visit visibility');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Update basic profile (username, first_name, last_name)
      const basicResponse = await fetch(`${API_BASE_URL}/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          username: profileData.username,
          first_name: profileData.first_name,
          last_name: profileData.last_name,
        }),
      });

      if (!basicResponse.ok) {
        const errorData = await basicResponse.json();
        throw new Error(errorData.detail || 'Failed to update profile');
      }

      // Update extended profile (display_name, bio, location, website_url, avatar_url)
      const extendedResponse = await fetch(`${API_BASE_URL}/users/social/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          display_name: profileData.display_name,
          bio: profileData.bio,
          location: profileData.location,
          website_url: profileData.website_url,
          avatar_url: profileData.avatar_url,
        }),
      });

      if (!extendedResponse.ok) {
        const errorData = await extendedResponse.json();
        throw new Error(errorData.detail || 'Failed to update extended profile');
      }

      setSuccess('Profile updated successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/users/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to change password');
      }

      setSuccess('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setDeleteError('');

    try {
      const response = await fetch(`${API_BASE_URL}/users/me`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete account');
      }

      // Account is gone — clear session and leave
      logout();
      navigate('/');
    } catch (err) {
      setDeleteError(err.message);
      setDeleteLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError('');
    setSuccess('');

    // Load first sub-tab data when activity tab is selected
    if (tab === 'restaurants' && activitySubTab === 'my-restaurants') {
      fetchMyRestaurants();
    }
  };

  const handleActivitySubTabChange = (subTab) => {
    setActivitySubTab(subTab);

    // Lazy load data based on sub-tab
    switch(subTab) {
      case 'my-restaurants':
        fetchMyRestaurants();
        break;
      case 'reviews':
        fetchMyReviews();
        break;
      case 'favorites':
        fetchMyFavorites();
        break;
      case 'wishlist':
        fetchMyWishlist();
        break;
      case 'visits':
        fetchMyVisits();
        break;
      default:
        break;
    }
  };

  if (loading && !profileData.email) {
    return (
      <Container className="my-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading profile...</p>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <h2 className="mb-4">My Profile</h2>

      <Card>
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

          <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-3">
            {/* Profile Info Tab */}
            <Tab eventKey="profile" title="Profile Info">
              <Form onSubmit={handleProfileUpdate}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={profileData.email}
                    disabled
                  />
                  <Form.Text className="text-muted">
                    Email cannot be changed at this time
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    type="text"
                    value={profileData.username}
                    onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
                    placeholder="Optional username"
                  />
                  <Form.Text className="text-muted">
                    Optional - used for display purposes
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={profileData.first_name}
                    onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={profileData.last_name}
                    onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                  />
                </Form.Group>

                <hr className="my-4" />
                <h5 className="mb-3">Extended Profile</h5>

                <Form.Group className="mb-3">
                  <Form.Label>Display Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={profileData.display_name}
                    onChange={(e) => setProfileData({ ...profileData, display_name: e.target.value })}
                    placeholder="How you want to be displayed to others"
                  />
                  <Form.Text className="text-muted">
                    This will be shown on your public profile and reviews
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Bio</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={profileData.bio}
                    onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                    placeholder="Tell others about yourself and your food preferences..."
                    maxLength={500}
                  />
                  <Form.Text className="text-muted">
                    {profileData.bio?.length || 0}/500 characters
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    value={profileData.location}
                    onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                    placeholder="City, State or Country"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Website URL</Form.Label>
                  <Form.Control
                    type="url"
                    value={profileData.website_url}
                    onChange={(e) => setProfileData({ ...profileData, website_url: e.target.value })}
                    placeholder="https://your-website.com"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Avatar URL</Form.Label>
                  <Form.Control
                    type="url"
                    value={profileData.avatar_url}
                    onChange={(e) => setProfileData({ ...profileData, avatar_url: e.target.value })}
                    placeholder="https://example.com/avatar.jpg"
                  />
                  <Form.Text className="text-muted">
                    URL to your profile picture
                  </Form.Text>
                </Form.Group>

                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Profile'}
                </Button>
              </Form>

              {/* Danger Zone */}
              <hr className="my-4" />
              <h5 className="text-danger">Danger Zone</h5>
              <p className="text-muted mb-2">
                Permanently delete your account, favorites, wishlist, visits, and profile.
                Your reviews remain, anonymized. This cannot be undone.
              </p>
              <Button variant="outline-danger" onClick={() => { setDeleteError(''); setShowDeleteModal(true); }}>
                Delete Account
              </Button>

              <Modal show={showDeleteModal} onHide={() => !deleteLoading && setShowDeleteModal(false)} centered>
                <Modal.Header closeButton={!deleteLoading}>
                  <Modal.Title>Delete Account?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {deleteError && <Alert variant="danger">{deleteError}</Alert>}
                  <p>
                    This permanently deletes your account and personal data
                    (favorites, wishlist, visits, profile, follows, notifications).
                    Your reviews will remain, anonymized.
                  </p>
                  <p className="fw-bold mb-0">This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
                    Cancel
                  </Button>
                  <Button variant="danger" onClick={handleDeleteAccount} disabled={deleteLoading}>
                    {deleteLoading ? 'Deleting…' : 'Delete My Account'}
                  </Button>
                </Modal.Footer>
              </Modal>
            </Tab>

            {/* Change Password Tab */}
            <Tab eventKey="password" title="Change Password">
              <Form onSubmit={handlePasswordChange}>
                <Form.Group className="mb-3">
                  <Form.Label>Current Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    required
                  />
                  <Form.Text className="text-muted">
                    Must be at least 6 characters
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Confirm New Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    required
                  />
                </Form.Group>

                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? 'Changing Password...' : 'Change Password'}
                </Button>
              </Form>
            </Tab>

            {/* Privacy Settings Tab */}
            <Tab eventKey="privacy" title="Privacy">
              <PrivacySettings />
            </Tab>

            {/* Follow Tab */}
            <Tab eventKey="followers" title="Follow">
              <FollowersList />
            </Tab>

            {/* My Activity Tab with Sub-Tabs */}
            <Tab eventKey="restaurants" title="My Activity">
              <Tabs
                activeKey={activitySubTab}
                onSelect={handleActivitySubTabChange}
                className="mb-3"
              >
                {/* My Restaurants Sub-Tab */}
                <Tab eventKey="my-restaurants" title="My Restaurants">
                  {restaurantsLoading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" />
                      <p className="mt-2">Loading restaurants...</p>
                    </div>
                  ) : myRestaurants.length > 0 ? (
                    <div className="row">
                      {myRestaurants.map((restaurant) => {
                        const cuisines = restaurant.cuisines?.slice(0, 2).map(c => c.name).join(' • ') || '';
                        const displayLocation = restaurant.locations?.find(loc => loc.is_main_location) || restaurant.locations?.[0];
                        const locationText = displayLocation ? displayLocation.city + (displayLocation.state ? `, ${displayLocation.state}` : '') : '';

                        return (
                          <div key={restaurant.id} className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-3">
                            <Card
                              className="shadow-sm h-100"
                              style={{ cursor: "pointer" }}
                              onClick={() => navigate(`/restaurant/${restaurant.id}/${generateSlug(restaurant.name)}`)}
                            >
                              <Card.Body className="p-3">
                                {/* Title and Status */}
                                <div className="d-flex justify-content-between align-items-start mb-1">
                                  <h6 className="mb-0 fw-bold text-primary" style={{ fontSize: "1rem" }}>
                                    {restaurant.name}
                                  </h6>
                                </div>

                                {/* #158: all three private states, from the shared
                                    helper. This copy previously showed a bare
                                    "🔒 Private" for BOTH admin-unpublished and
                                    just-for-me, with no sub-text to tell them
                                    apart — the worst of the three copies. */}
                                {(() => {
                                  const status = ownershipStatus(restaurant);
                                  if (!status) return null;
                                  return (
                                    <div className="mb-2">
                                      <Badge bg={status.variant} text={status.variant === 'warning' ? 'dark' : undefined}>
                                        {status.icon} {status.label}
                                      </Badge>
                                      <small className="text-muted d-block mt-1">{status.hint}</small>
                                    </div>
                                  );
                                })()}

                                {/* Cuisine and Location */}
                                <div className="text-muted small mb-2">
                                  {cuisines && <span>{cuisines}</span>}
                                  {cuisines && locationText && <span> • </span>}
                                  {locationText && <span>{locationText}</span>}
                                </div>

                                {/* Image Thumbnails */}
                                <div className="d-flex gap-2 overflow-hidden" style={{ height: "60px" }}>
                                  {(() => {
                                    let displayImages = [];
                                    if (restaurant.menu_item_images && restaurant.menu_item_images.length > 0) {
                                      displayImages = restaurant.menu_item_images.slice(0, 4);
                                    }
                                    if (displayImages.length < 4 && restaurant.images && restaurant.images.length > 0) {
                                      const remaining = 4 - displayImages.length;
                                      displayImages = [...displayImages, ...restaurant.images.slice(0, remaining)];
                                    }

                                    return displayImages.length > 0 ? (
                                      displayImages.map((img, idx) => (
                                        <div
                                          key={idx}
                                          className="border rounded flex-shrink-0 overflow-hidden"
                                          style={{ width: "80px", height: "60px" }}
                                        >
                                          <img
                                            src={img.flickr_url}
                                            alt={`${restaurant.name} ${idx + 1}`}
                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                          />
                                        </div>
                                      ))
                                    ) : (
                                      <div className="bg-light border rounded flex-fill d-flex align-items-center justify-content-center text-muted" style={{ height: "60px" }}>
                                        <span className="small">No photos yet</span>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </Card.Body>
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-3 bg-light rounded">
                      <p className="text-muted mb-2">No restaurants added yet</p>
                      <Button variant="outline-primary" size="sm" onClick={() => navigate('/add-restaurant')}>
                        Add Restaurant
                      </Button>
                    </div>
                  )}
                </Tab>

                {/* My Reviews Sub-Tab */}
                <Tab eventKey="reviews" title="My Reviews">
                  {reviewsLoading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" />
                      <p className="mt-2">Loading reviews...</p>
                    </div>
                  ) : myReviews.length > 0 ? (
                    groupReviewsByVisit(myReviews).map((group) => {
                      // #110: a multi-dish visit fans out to one review per dish;
                      // show it as ONE card with each dish (name + rating + photos +
                      // its own edit/delete) inside. Singletons keep the card below.
                      if (group.length > 1) {
                        const head = group[0];
                        return (
                          <Card key={`visit-${head.visit_id}`} className="mb-3">
                            <Card.Body>
                              {/* Visit header — the container; dishes fold in below. */}
                              <div className="mb-2 d-flex align-items-center flex-wrap gap-2">
                                <Badge bg="light" text="dark" className="border">🍽️ Visit</Badge>
                                <span className="fw-semibold">{head.restaurant?.name || 'Restaurant'}</span>
                                <span className="text-muted small">on {new Date(head.visit_date || head.created_at).toLocaleDateString()}</span>
                              </div>
                              {/* #110: the visit's overall experience rating, distinct
                                  from the per-dish ratings below. Only when set. */}
                              {head.visit_experience_rating > 0 && (
                                <div className="mb-2">
                                  <Badge bg="primary">★ {head.visit_experience_rating}/5</Badge>
                                  <small className="text-muted ms-2">Overall experience</small>
                                </div>
                              )}
                              {head.title && <p className="mb-1 small"><strong>{head.title}</strong></p>}
                              {head.review_text && <p className="mb-2 small">{head.review_text}</p>}
                              {/* Dishes folded INTO this visit — tinted, labelled panel. */}
                              <div className="bg-light rounded border">
                                <div className="px-3 pt-2 pb-1 text-muted text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.04em' }}>
                                  {group.length} {group.length === 1 ? 'dish' : 'dishes'} on this visit
                                </div>
                                {group.map((r, i) => (
                                  <div key={r.id} className={`px-3 py-2 ${i > 0 ? 'border-top' : ''}`}>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <span className="small fw-semibold">{reviewDishName(r) || 'Dish'}</span>
                                      <div className="d-flex align-items-center gap-1">
                                        <Badge bg="primary">★ {r.rating}/5</Badge>
                                        <Button size="sm" variant="link" className="text-secondary p-0" onClick={() => handleEditReview(r)} title="Edit review" style={{ fontSize: '1rem', lineHeight: '1', minWidth: 'auto', border: 'none' }}>✏️</Button>
                                        <Button size="sm" variant="link" className="text-danger p-0" onClick={() => handleDeleteReview(r.id)} title="Delete review" style={{ fontSize: '1rem', lineHeight: '1', minWidth: 'auto', border: 'none' }}>🗑️</Button>
                                      </div>
                                    </div>
                                    {/* #230: framed to the dish THIS review is for, so a
                                        table shot shared across dishes shows the right one
                                        in each row. Matches VisitReviewGroupCard. */}
                                    {r.photos && r.photos.length > 0 && (
                                      <div className="d-flex flex-wrap gap-2 mt-2">
                                        {r.photos.map((photo) => (
                                          <DishPhoto
                                            key={photo.id}
                                            photo={photo}
                                            visitDishId={r.visit_dish_id}
                                            alt={reviewDishName(r) || 'Visit'}
                                            size={64}
                                          />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </Card.Body>
                          </Card>
                        );
                      }
                      const review = group[0];
                      // Determine what this review is for
                      const reviewTarget = review.food?.name || review.menu_item?.food?.name || review.restaurant?.name || 'Item';
                      const reviewType = review.food?.name ? 'Food' : review.menu_item?.food?.name ? 'Menu Item' : 'Restaurant';

                      return (
                        <Card key={review.id} className="mb-3">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div className="flex-grow-1">
                                <h6 className="mb-0">{reviewTarget}</h6>
                                {reviewType !== 'Restaurant' && (
                                  <small className="text-muted">
                                    {reviewType} at {review.restaurant?.name || 'Restaurant'}
                                  </small>
                                )}
                              </div>
                              <div className="d-flex align-items-center gap-1">
                                <Badge bg="primary">★ {review.rating}/5</Badge>
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="text-secondary p-0"
                                  onClick={() => handleEditReview(review)}
                                  title="Edit review"
                                  style={{ fontSize: '1rem', lineHeight: '1', minWidth: 'auto', border: 'none' }}
                                >
                                  ✏️
                                </Button>
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="text-danger p-0"
                                  onClick={() => handleDeleteReview(review.id)}
                                  title="Delete review"
                                  style={{ fontSize: '1rem', lineHeight: '1', minWidth: 'auto', border: 'none' }}
                                >
                                  🗑️
                                </Button>
                              </div>
                            </div>
                            {review.title && <p className="mb-1 small"><strong>{review.title}</strong></p>}
                            <p className="mb-2 small">{review.review_text}</p>
                            {/* Photos from the linked visit (#111 part 1) — a review
                                promoted from a visit surfaces that visit's photos back
                                to its own author. Owner-gated server-side; managed on
                                the visit, so these are read-only here (click = full size). */}
                            {review.photos && review.photos.length > 0 && (
                              <div className="d-flex flex-wrap gap-2 mb-2">
                                {review.photos.map((photo) => (
                                  // #230: a review with no dish (visit_dish_id null)
                                  // shows the whole shot, exactly as before.
                                  <DishPhoto
                                    key={photo.id}
                                    photo={photo}
                                    visitDishId={review.visit_dish_id}
                                    alt="Visit"
                                    size={64}
                                  />
                                ))}
                              </div>
                            )}
                            <small className="text-muted">
                              {new Date(review.created_at).toLocaleDateString()}
                            </small>
                          </Card.Body>
                        </Card>
                      );
                    })
                  ) : (
                    <div className="text-center py-3 bg-light rounded">
                      <p className="text-muted mb-2">No reviews yet</p>
                      <Button variant="outline-primary" size="sm" onClick={() => navigate('/restaurants')}>
                        Browse Restaurants
                      </Button>
                    </div>
                  )}
                </Tab>

                {/* My Favorites Sub-Tab */}
                <Tab eventKey="favorites" title="❤️ Favorites">
                  {favoritesLoading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" />
                      <p className="mt-2">Loading favorites...</p>
                    </div>
                  ) : myFavorites.length > 0 ? (
                    <div className="row">
                      {myFavorites.map((favorite) => {
                        // Dish favorite (#102) — link to /food/:id, not a restaurant.
                        if (favorite.food_id) {
                          return (
                            <div key={`food-${favorite.id}`} className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-3">
                              <Card
                                className="shadow-sm h-100"
                                style={{ cursor: "pointer" }}
                                onClick={() => navigate(`/food/${favorite.food_id}`)}
                              >
                                <Card.Body className="p-3">
                                  <div className="mb-1">
                                    <h6 className="mb-0 fw-bold text-primary" style={{ fontSize: "1rem" }}>
                                      🍽️ {favorite.food_name || 'Dish'}
                                    </h6>
                                  </div>
                                  <div className="text-muted small mb-2">
                                    ❤️ Added {new Date(favorite.created_at).toLocaleDateString()}
                                  </div>
                                  <div className="d-flex gap-2 overflow-hidden" style={{ height: "60px" }}>
                                    {favorite.food_image_url ? (
                                      <div style={{ width: "80px", minWidth: "80px", height: "60px", backgroundImage: `url(${favorite.food_image_url})`, backgroundSize: "cover", backgroundPosition: "center", borderRadius: "4px" }} />
                                    ) : (
                                      <div className="bg-light border rounded flex-fill d-flex align-items-center justify-content-center text-muted" style={{ height: "60px" }}>
                                        <span className="small">No photos yet</span>
                                      </div>
                                    )}
                                  </div>
                                </Card.Body>
                              </Card>
                            </div>
                          );
                        }

                        const locationText = favorite.restaurant_city ?
                          favorite.restaurant_city + (favorite.restaurant_state ? `, ${favorite.restaurant_state}` : '') : '';

                        return (
                          <div key={favorite.id} className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-3">
                            <Card
                              className="shadow-sm h-100"
                              style={{ cursor: "pointer" }}
                              onClick={() => navigate(`/restaurant/${favorite.restaurant_id}/${generateSlug(favorite.restaurant_name)}`)}
                            >
                              <Card.Body className="p-3">
                                {/* Title */}
                                <div className="mb-1">
                                  <h6 className="mb-0 fw-bold text-primary" style={{ fontSize: "1rem" }}>
                                    {favorite.restaurant_name || 'Restaurant'}
                                  </h6>
                                </div>

                                {/* Location */}
                                {locationText && (
                                  <div className="text-muted small mb-2">
                                    <span>{locationText}</span>
                                  </div>
                                )}

                                {/* Date Added */}
                                <div className="text-muted small mb-2">
                                  ❤️ Added {new Date(favorite.created_at).toLocaleDateString()}
                                </div>

                                {/* Restaurant images */}
                                <div className="d-flex gap-2 overflow-hidden" style={{ height: "60px" }}>
                                  {favorite.menu_item_images && favorite.menu_item_images.length > 0 ? (
                                    favorite.menu_item_images.slice(0, 4).map((img, idx) => (
                                      <div
                                        key={idx}
                                        style={{
                                          width: "80px",
                                          minWidth: "80px",
                                          height: "60px",
                                          backgroundImage: `url(${img.flickr_url})`,
                                          backgroundSize: "cover",
                                          backgroundPosition: "center",
                                          borderRadius: "4px"
                                        }}
                                      />
                                    ))
                                  ) : (
                                    <div className="bg-light border rounded flex-fill d-flex align-items-center justify-content-center text-muted" style={{ height: "60px" }}>
                                      <span className="small">No photos yet</span>
                                    </div>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-3 bg-light rounded">
                      <p className="text-muted mb-0 small">No favorites yet</p>
                    </div>
                  )}
                </Tab>

                {/* Want to Try Sub-Tab */}
                <Tab eventKey="wishlist" title="📍 Want to Try">
                  {wishlistLoading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" />
                      <p className="mt-2">Loading wishlist...</p>
                    </div>
                  ) : myWishlist.length > 0 ? (
                    <div className="row">
                      {myWishlist.map((item) => {
                        // Dish on wishlist (#102) — link to /food/:id, not a restaurant.
                        if (item.food_id) {
                          return (
                            <div key={`food-${item.id}`} className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-3">
                              <Card
                                className="shadow-sm h-100"
                                style={{ cursor: "pointer" }}
                                onClick={() => navigate(`/food/${item.food_id}`)}
                              >
                                <Card.Body className="p-3">
                                  <Card.Title className="h6 mb-2" style={{ fontSize: "1rem", fontWeight: "600" }}>
                                    🍽️ {item.food_name || 'Dish'}
                                  </Card.Title>
                                  {item.notes && (
                                    <div className="text-muted small mb-2">
                                      <strong>Note:</strong> {item.notes}
                                    </div>
                                  )}
                                  <div className="text-muted small mb-3">
                                    📍 Added {new Date(item.created_at).toLocaleDateString()}
                                  </div>
                                  <div className="d-flex gap-2 overflow-hidden" style={{ height: "60px" }}>
                                    {item.food_image_url ? (
                                      <div style={{ width: "80px", minWidth: "80px", height: "60px", backgroundImage: `url(${item.food_image_url})`, backgroundSize: "cover", backgroundPosition: "center", borderRadius: "4px" }} />
                                    ) : (
                                      <div className="bg-light border rounded flex-fill d-flex align-items-center justify-content-center text-muted" style={{ height: "60px" }}>
                                        <span className="small">No photos yet</span>
                                      </div>
                                    )}
                                  </div>
                                </Card.Body>
                              </Card>
                            </div>
                          );
                        }

                        const locationText = [item.restaurant_city, item.restaurant_state].filter(Boolean).join(', ');

                        return (
                          <div key={item.id} className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-3">
                            <Card
                              className="shadow-sm h-100"
                              style={{ cursor: "pointer" }}
                              onClick={() => navigate(`/restaurant/${item.restaurant_id}/${generateSlug(item.restaurant_name)}`)}
                            >
                              <Card.Body className="p-3">
                                <Card.Title className="h6 mb-2" style={{
                                  fontSize: "1rem",
                                  fontWeight: "600",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap"
                                }}>
                                  {item.restaurant_name || 'Restaurant'}
                                </Card.Title>

                                {locationText && (
                                  <div className="text-muted small mb-2" style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                  }}>
                                    {locationText}
                                  </div>
                                )}

                                {item.notes && (
                                  <div className="text-muted small mb-2" style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                  }}>
                                    <strong>Note:</strong> {item.notes}
                                  </div>
                                )}

                                <div className="text-muted small mb-3">
                                  📍 Added {new Date(item.created_at).toLocaleDateString()}
                                </div>

                                {/* Image thumbnails */}
                                <div className="d-flex gap-2 overflow-hidden" style={{ height: "60px" }}>
                                  {item.menu_item_images && item.menu_item_images.length > 0 ? (
                                    item.menu_item_images.slice(0, 4).map((img, idx) => (
                                      <div
                                        key={idx}
                                        style={{
                                          width: "80px",
                                          minWidth: "80px",
                                          height: "60px",
                                          backgroundImage: `url(${img.flickr_url})`,
                                          backgroundSize: "cover",
                                          backgroundPosition: "center",
                                          borderRadius: "4px"
                                        }}
                                      />
                                    ))
                                  ) : (
                                    <div className="bg-light border rounded flex-fill d-flex align-items-center justify-content-center text-muted" style={{ height: "60px" }}>
                                      <span className="small">No photos yet</span>
                                    </div>
                                  )}
                                </div>
                              </Card.Body>
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-3 bg-light rounded">
                      <p className="text-muted mb-0 small">No restaurants in wishlist</p>
                    </div>
                  )}
                </Tab>

                {/* My Visits Sub-Tab */}
                <Tab eventKey="visits" title="🍽️ Visits">
                  <div className="d-flex justify-content-end mb-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={fetchMyVisits}
                      disabled={visitsLoading}
                    >
                      {visitsLoading ? <Spinner animation="border" size="sm" /> : '↻ Refresh'}
                    </Button>
                  </div>
                  {visitsLoading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" />
                      <p className="mt-2">Loading visits...</p>
                    </div>
                  ) : myVisits.length > 0 ? (
                    (() => {
                      // Group visits by restaurant
                      const visitsByRestaurant = myVisits.reduce((acc, visit) => {
                        const restaurantKey = visit.restaurant_id;
                        if (!acc[restaurantKey]) {
                          acc[restaurantKey] = {
                            restaurantId: visit.restaurant_id,
                            restaurantName: visit.restaurant_name || `Restaurant ${visit.restaurant_id}`,
                            visits: []
                          };
                        }
                        acc[restaurantKey].visits.push(visit);
                        return acc;
                      }, {});

                      // Sort visits within each restaurant by visit_date descending (newest first)
                      Object.values(visitsByRestaurant).forEach(group => {
                        group.visits.sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date));
                      });

                      // Sort restaurant groups by most recent visit date
                      const sortedRestaurants = Object.values(visitsByRestaurant).sort((a, b) => {
                        const mostRecentA = new Date(a.visits[0].visit_date);
                        const mostRecentB = new Date(b.visits[0].visit_date);
                        return mostRecentB - mostRecentA;
                      });

                      return sortedRestaurants.map((group) => (
                        <div key={group.restaurantId} className="mb-4">
                          {/* Restaurant Section Header */}
                          <h5 className="mb-3 pb-2 border-bottom">
                            {group.restaurantName}
                            <Badge bg="secondary" className="ms-2 small">{group.visits.length} visit{group.visits.length !== 1 ? 's' : ''}</Badge>
                          </h5>

                          {/* Visits List for this Restaurant */}
                          {group.visits.map((visit) => {
                            // #230: THIS view never got #198's read path — it went
                            // into the near-identically-named UserProfilePage.js, and
                            // /profile/<your own id> redirects here, so there was no
                            // route by which the author of a visit could see their own
                            // multi-dish photos. Same logic as the public profile now,
                            // shared via utils/visitPhotos rather than copied again.
                            const visitDishes = Array.isArray(visit.dishes) ? visit.dishes : [];
                            const { photosByDish, loosePhotos } =
                              groupPhotosByDish(visit.photos, visitDishes);
                            const dishesWithPhotos = visitDishes.filter(
                              (d) => (photosByDish[d.id] || []).length > 0
                            );
                            // The summary line above already names the dish when there
                            // is only one, so repeating it over the thumbnails is noise.
                            const showDishLabels = dishesWithPhotos.length > 1;
                            return (
                            <Card key={visit.id} className="mb-3 ms-3">
                              <Card.Body>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                      {visit.is_public ? (
                                        <Badge bg="success" className="small">📢 Public Review</Badge>
                                      ) : (
                                        <Badge bg="secondary" className="small">🔒 Private</Badge>
                                      )}
                                    </div>
                                    {/* #110: the dishes logged on this visit, each with its own
                                        rating. Falls back to the legacy single dish name for
                                        older cached visits that predate the dishes array. */}
                                    {Array.isArray(visit.dishes) && visit.dishes.length > 0 ? (
                                      <p className="text-muted mb-1 small">
                                        🍽️ {visit.dishes.map((d, i) => {
                                          const nm = d.custom_name || d.menu_item_name || d.food_name || d.dish_name || 'Dish';  // #119
                                          return (
                                            <span key={d.id || i}>
                                              {i > 0 ? ', ' : ''}{nm}{d.rating ? ` ★${d.rating}` : ''}
                                            </span>
                                          );
                                        })}
                                      </p>
                                    ) : (visit.food_name || visit.dish_name) ? (
                                      <p className="text-muted mb-1 small">🍽️ {visit.food_name || visit.dish_name}</p>
                                    ) : null}
                                    <p className="text-muted mb-1 small">
                                      {new Date(visit.visit_date).toLocaleDateString()}
                                      {visit.meal_type && ` • ${visit.meal_type.charAt(0).toUpperCase() + visit.meal_type.slice(1)}`}
                                    </p>
                                    {/* Which location was visited (#63) — only shown when the
                                        visit captured a location_id (#41). */}
                                    {(visit.location_name || visit.location_city || visit.location_address) && (
                                      <p className="text-muted mb-2 small">
                                        📍 {[
                                          visit.location_name || visit.location_address,
                                          [visit.location_city, visit.location_state].filter(Boolean).join(', ')
                                        ].filter(Boolean).join(' — ')}
                                      </p>
                                    )}
                                  </div>
                                  <div className="d-flex align-items-center gap-1">
                                    {visit.rating && (
                                      <Badge bg="primary">★ {visit.rating}/5</Badge>
                                    )}
                                    {/* #171: no make-public control for a visit to a
                                        "just for me" restaurant — it has no public
                                        page for a review to appear on. A PENDING
                                        submission keeps it: the review goes live
                                        when the restaurant is approved. Already-
                                        public visits keep it either way, so one can
                                        always be turned back off. */}
                                    {(visit.is_public
                                      || visit.restaurant_visibility !== 'private'
                                      || visit.restaurant_review_state === 'pending') && (
                                    <Button
                                      size="sm"
                                      variant="link"
                                      className="text-secondary p-0"
                                      onClick={() => handleToggleVisitPublic(visit)}
                                      title={visit.is_public ? "Make private" : "Make public"}
                                      style={{ fontSize: '1rem', lineHeight: '1', minWidth: 'auto', border: 'none' }}
                                    >
                                      {visit.is_public ? '🔓' : '🔒'}
                                    </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="link"
                                      className="text-secondary p-0"
                                      onClick={() => handleEditVisit(visit)}
                                      title="Edit visit"
                                      style={{ fontSize: '1rem', lineHeight: '1', minWidth: 'auto', border: 'none' }}
                                    >
                                      ✏️
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="link"
                                      className="text-danger p-0"
                                      onClick={() => handleDeleteVisit(visit.id)}
                                      title="Delete visit"
                                      style={{ fontSize: '1rem', lineHeight: '1', minWidth: 'auto', border: 'none' }}
                                    >
                                      🗑️
                                    </Button>
                                  </div>
                                </div>
                                {visit.notes && (
                                  <p className="mb-2 small">{visit.notes}</p>
                                )}
                                {/* Visit photos (#109), grouped by the dish each one is
                                    of and framed to it by that dish's crop (#198/#230).
                                    One table shot naming three dishes is ONE upload and
                                    appears in all three rows. Click still opens the edit
                                    modal, where photos are added/removed — that is this
                                    view's job, unlike the public profile where a click
                                    opens the original. */}
                                {(dishesWithPhotos.length > 0 || loosePhotos.length > 0) && (
                                  <div className="mb-2">
                                    {dishesWithPhotos.map((d) => (
                                      <div key={d.id} className="mb-2">
                                        {showDishLabels && (
                                          <div className="text-muted small mb-1">{visitDishLabel(d)}</div>
                                        )}
                                        <div className="d-flex flex-wrap gap-2">
                                          {photosByDish[d.id].map((photo) => (
                                            <DishPhoto
                                              key={`${photo.id}-${d.id}`}
                                              photo={photo}
                                              visitDishId={d.id}
                                              alt={visitDishLabel(d)}
                                              size={64}
                                              onClick={() => handleEditVisit(visit)}
                                            />
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                    {loosePhotos.length > 0 && (
                                      <div className="d-flex flex-wrap gap-2">
                                        {loosePhotos.map((photo) => (
                                          <DishPhoto
                                            key={`${photo.id}-loose`}
                                            photo={photo}
                                            alt="Visit"
                                            size={64}
                                            onClick={() => handleEditVisit(visit)}
                                          />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <small className="text-muted">
                                  Visit #{visit.id} • Created {new Date(visit.created_at).toLocaleDateString()}
                                </small>
                              </Card.Body>
                            </Card>
                            );
                          })}
                        </div>
                      ));
                    })()
                  ) : (
                    <div className="text-center py-3 bg-light rounded">
                      <p className="text-muted mb-0 small">No visits logged yet</p>
                    </div>
                  )}
                </Tab>
              </Tabs>
            </Tab>

          </Tabs>
        </Card.Body>
      </Card>

      {/* Edit Visit Modal */}
      <EditVisitModal
        show={showEditVisitModal}
        onHide={() => {
          setShowEditVisitModal(false);
          setEditingVisit(null);
        }}
        visit={editingVisit}
        onVisitUpdated={handleVisitUpdated}
      />

      {/* Edit Review Modal */}
      <EditReviewModal
        show={showEditReviewModal}
        onHide={() => {
          setShowEditReviewModal(false);
          setEditingReview(null);
        }}
        review={editingReview}
        onReviewUpdated={handleReviewUpdated}
      />
    </Container>
  );
}

export default UserProfile;
